import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
} from "@mui/material";

import { useRedux } from "../../../hooks";
import { submitFeedbackThunk } from "../../../redux/process/actions";
import {
  FeedbackEntry,
  SubmitFeedbackPayload,
  FileDetails,
} from "../../../types/process";
import FileInput from "../../../components/process/UI/FileInput";
import { DEFAULT_FILE_CONFIG } from "../../../constants/process";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: {
    type: "category" | "step";
    id: string;
    name: string;
    feedbackHistory?: FeedbackEntry[];
  } | null;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  target,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { dispatch, useAppSelector } = useRedux();

  const [feedbackText, setFeedbackText] = useState("");
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const { loading: workflowLoading } = useAppSelector(
    (state: any) => state.Process?.workflows || { loading: false },
  );

  useEffect(() => {
    if (isOpen) {
      setFeedbackText("");
      setNewAttachments([]);
      setSubmitMessage(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !target) return null;

  const handleAttachmentChange = (files: File[]) => {
    setNewAttachments(files);
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) {
      setSubmitMessage({ type: "error", text: t("feedback_cannot_be_empty") });
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage(null);

    const payload: SubmitFeedbackPayload = {
      targetType: target.type,
      targetId: target.id,
      feedbackText,
      newAttachments: newAttachments.length > 0 ? newAttachments : undefined,
    };

    try {
      await dispatch(submitFeedbackThunk(payload));
      setSubmitMessage({
        type: "success",
        text: t("feedback_submitted_successfully"),
      });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setSubmitMessage({ type: "error", text: t("failed_to_submit_feedback") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFeedbackHistory = (feedbackHistory?: FeedbackEntry[]) => {
    if (!feedbackHistory || feedbackHistory.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          {t("no_feedback_history")}
        </Typography>
      );
    }
    return (
      <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
        {feedbackHistory
          .slice()
          .reverse()
          .map(entry => (
            <Paper
              key={entry.id}
              sx={{ p: 2, mb: 1, backgroundColor: theme.palette.grey[50] }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {entry.text}
              </Typography>
              {entry.attachments && entry.attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    {t("attachments")}:
                  </Typography>
                  {entry.attachments.map(file => (
                    <Typography
                      key={file.id}
                      variant="caption"
                      sx={{ display: "block", ml: 2 }}
                    >
                      • {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </Typography>
                  ))}
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "right" }}
              >
                - {entry.author} {t("on")}{" "}
                {new Date(entry.date).toLocaleDateString()}
              </Typography>
            </Paper>
          ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmitFeedback,
      }}
    >
      <DialogTitle>
        {t("provide_feedback_for", {
          type: target.type === "category" ? t("category") : t("step"),
          name: target.name,
        })}
      </DialogTitle>
      <DialogContent dividers>
        {target.feedbackHistory && target.feedbackHistory.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("feedback_history")}:
            </Typography>
            {renderFeedbackHistory(target.feedbackHistory)}
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        <TextField
          label={t("your_new_feedback")}
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          placeholder={t("enter_feedback_placeholder", { name: target.name })}
          multiline
          rows={5}
          fullWidth
          required
          disabled={isSubmitting || workflowLoading}
          sx={{ mb: 2 }}
        />

        <FileInput
          id={`feedback-attachments-${target.id}`}
          label={t("attach_files_optional")}
          fieldName="feedbackAttachments"
          config={{ ...DEFAULT_FILE_CONFIG, maxFiles: 3 }}
          onChange={handleAttachmentChange}
        />

        {submitMessage && (
          <Alert severity={submitMessage.type} sx={{ mt: 2 }}>
            {submitMessage.text}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting || workflowLoading}>
          {t("cancel")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || workflowLoading || !feedbackText.trim()}
        >
          {isSubmitting || workflowLoading ? (
            <CircularProgress size={24} sx={{ color: "inherit" }} />
          ) : (
            t("submit_feedback")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackModal;
